import React from 'react';
import { styled } from '@compiled/react';
import { graphql, useFragment } from 'react-relay';
import { Box, xcss } from '@atlaskit/primitives';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
// eslint-disable-next-line jira/restricted/@atlassian+jira-common-styles
import { gridSize } from '@atlassian/jira-common-styles/src';
import type { approvalDrawer_softwareReleasesApprovers_jiraVersion$key } from '@atlassian/jira-relay/src/__generated__/approvalDrawer_softwareReleasesApprovers_jiraVersion.graphql';
import type { approvalDrawer_softwareReleasesApprovers_jiraVersionApprover$key } from '@atlassian/jira-relay/src/__generated__/approvalDrawer_softwareReleasesApprovers_jiraVersionApprover.graphql';
import { ApprovalDescription } from './approval-description';
import { DeclineReason } from './decline-reason';
import { RemoveApprover } from './remove-approver';

type Props = {
	jiraVersionApprover: approvalDrawer_softwareReleasesApprovers_jiraVersionApprover$key;
	jiraVersion: approvalDrawer_softwareReleasesApprovers_jiraVersion$key;
};

export const ApprovalDrawer = ({ jiraVersionApprover, jiraVersion }: Props) => {
	const approver = useFragment(
		graphql`
			fragment approvalDrawer_softwareReleasesApprovers_jiraVersionApprover on JiraVersionApprover {
				status @required(action: THROW)
				...declineReason_softwareReleasesApprovers
				...approvalDescription_softwareReleasesApprovers
				...removeApprover_softwareReleasesApprovers_jiraVersionApprover
			}
		`,
		jiraVersionApprover,
	);

	const jiraVersionData = useFragment(
		graphql`
			fragment approvalDrawer_softwareReleasesApprovers_jiraVersion on JiraVersion {
				...removeApprover_softwareReleasesApprovers_jiraVersion
			}
		`,
		jiraVersion,
	);

	return (
		<Wrapper>
			<TopWrapper>
				{approver.status === 'DECLINED' && (
					<Box xcss={declineReasonWrapperStyles}>
						<DeclineReason jiraVersionApprover={approver} />
					</Box>
				)}
				<ApprovalDescription jiraVersionApprover={approver} />
			</TopWrapper>
			<RemoveApprover jiraVersionApprover={approver} jiraVersion={jiraVersionData} />
		</Wrapper>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Wrapper = styled.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	width: `${gridSize * 41.5}px`,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const TopWrapper = styled.div({
	width: '100%',
	boxSizing: 'border-box',
	padding: token('space.200', '16px'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	borderBottom: `1px solid ${token('color.border', colors.N40)}`,
});

const declineReasonWrapperStyles = xcss({
	marginBottom: 'space.300',
});
